import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import TrendsAnalysis from '@/components/TrendsAnalysis';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileSearch, Bell, TrendingUp, Clock, AlertCircle, ArrowRight, Euro, MapPin } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateLocalized, formatRelativeTime } from '@/hooks/useLocationDetection';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

interface DashboardStats {
  totalSearches: number;
  activeAlerts: number;
  newResults: number;
  pendingDeadlines: number;
  totalResults: number;
}

interface CountryData {
  country: string;
  count: number;
}

interface TimelineData {
  date: string;
  count: number;
}

interface ValueRangeData {
  range: string;
  count: number;
}

const CHART_COLORS = ['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(var(--warning))', 'hsl(var(--destructive))', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];

export default function Dashboard() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentResults, setRecentResults] = useState<any[]>([]);
  const [countryData, setCountryData] = useState<CountryData[]>([]);
  const [timelineData, setTimelineData] = useState<TimelineData[]>([]);
  const [valueRangeData, setValueRangeData] = useState<ValueRangeData[]>([]);
  const [loading, setLoading] = useState(true);

  // Handle checkout success parameter
  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      toast.success(t('dashboard.checkoutSuccess'), {
        description: t('dashboard.checkoutSuccessDescription'),
        duration: 6000,
      });
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch searches count
        const { count: searchesCount } = await supabase
          .from('ted_searches')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true);

        // Fetch alerts count
        const { count: alertsCount } = await supabase
          .from('alerts')
          .select('*', { count: 'exact', head: true })
          .eq('email_enabled', true);

        // Fetch unread results count
        const { count: unreadCount } = await supabase
          .from('ted_results')
          .select('*', { count: 'exact', head: true })
          .eq('is_read', false);

        // Fetch results with upcoming deadlines
        const now = new Date().toISOString();
        const { count: deadlinesCount } = await supabase
          .from('ted_results')
          .select('*', { count: 'exact', head: true })
          .gte('deadline_date', now);

        // Fetch total results
        const { count: totalCount } = await supabase
          .from('ted_results')
          .select('*', { count: 'exact', head: true });

        setStats({
          totalSearches: searchesCount || 0,
          activeAlerts: alertsCount || 0,
          newResults: unreadCount || 0,
          pendingDeadlines: deadlinesCount || 0,
          totalResults: totalCount || 0,
        });

        // Fetch recent results
        const { data: results } = await supabase
          .from('ted_results')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(5);

        setRecentResults(results || []);

        // Fetch all results for analytics
        const { data: allResults } = await supabase
          .from('ted_results')
          .select('country, publication_date, estimated_value, created_at')
          .order('created_at', { ascending: false })
          .limit(500);

        if (allResults && allResults.length > 0) {
          // Country distribution
          const countryMap = new Map<string, number>();
          allResults.forEach(r => {
            if (r.country) {
              countryMap.set(r.country, (countryMap.get(r.country) || 0) + 1);
            }
          });
          const sortedCountries = Array.from(countryMap.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([country, count]) => ({ country, count }));
          setCountryData(sortedCountries);

          // Timeline (last 30 days)
          const timelineMap = new Map<string, number>();
          const last30Days = new Date();
          last30Days.setDate(last30Days.getDate() - 30);
          
          allResults.forEach(r => {
            if (r.created_at) {
              const date = new Date(r.created_at);
              if (date >= last30Days) {
                const dateStr = date.toISOString().split('T')[0];
                timelineMap.set(dateStr, (timelineMap.get(dateStr) || 0) + 1);
              }
            }
          });
          
          const currentLang = i18n.language?.split('-')[0] || 'en';
          const sortedTimeline = Array.from(timelineMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, count]) => ({
              date: new Date(date).toLocaleDateString(currentLang, { day: '2-digit', month: 'short' }),
              count,
            }));
          setTimelineData(sortedTimeline);

          // Value range distribution
          const valueRanges = [
            { range: '< 50k', min: 0, max: 50000, count: 0 },
            { range: '50k-100k', min: 50000, max: 100000, count: 0 },
            { range: '100k-500k', min: 100000, max: 500000, count: 0 },
            { range: '500k-1M', min: 500000, max: 1000000, count: 0 },
            { range: '> 1M', min: 1000000, max: Infinity, count: 0 },
          ];
          
          allResults.forEach(r => {
            if (r.estimated_value) {
              const range = valueRanges.find(vr => r.estimated_value >= vr.min && r.estimated_value < vr.max);
              if (range) range.count++;
            }
          });
          
          setValueRangeData(valueRanges.filter(r => r.count > 0).map(({ range, count }) => ({ range, count })));
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [i18n.language]);

  const statCards = [
    {
      title: t('dashboard.activeSearches'),
      value: stats?.totalSearches || 0,
      icon: FileSearch,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      link: '/monitoring',
    },
    {
      title: t('dashboard.configuredAlerts'),
      value: stats?.activeAlerts || 0,
      icon: Bell,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      link: '/monitoring',
    },
    {
      title: t('dashboard.newResults'),
      value: stats?.newResults || 0,
      icon: TrendingUp,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      link: '/results',
    },
    {
      title: t('dashboard.openDeadlines'),
      value: stats?.pendingDeadlines || 0,
      icon: Clock,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      link: '/results',
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('dashboard.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('dashboard.subtitle')}
            </p>
          </div>
          <Button onClick={() => navigate('/search')}>
            <FileSearch className="w-4 h-4 mr-2" />
            {t('dashboard.newSearch')}
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => (
            <Card 
              key={stat.title} 
              className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(stat.link)}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div>
                    {loading ? (
                      <Skeleton className="h-8 w-16" />
                    ) : (
                      <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Timeline Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                {t('dashboard.tendersFound30Days')}
              </CardTitle>
              <CardDescription>
                {t('dashboard.tendersTimeline')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : timelineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      labelStyle={{ color: 'hsl(var(--foreground))' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2 }}
                      name={t('dashboard.tenders')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  {t('dashboard.noDataAvailable')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Country Distribution */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                {t('dashboard.countryDistribution')}
              </CardTitle>
              <CardDescription>
                {t('dashboard.countryDistributionDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : countryData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={countryData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      type="number" 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="country" 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      labelStyle={{ color: 'hsl(var(--foreground))' }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="hsl(var(--primary))" 
                      radius={[0, 4, 4, 0]}
                      name={t('dashboard.tenders')}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  {t('dashboard.noDataAvailable')}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Value Distribution & Recent Results */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Value Distribution */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Euro className="w-5 h-5 text-primary" />
                {t('dashboard.valueDistribution')}
              </CardTitle>
              <CardDescription>
                {t('dashboard.valueDistributionDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : valueRangeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={valueRangeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="range"
                      label={({ range, percent }) => `${range} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {valueRangeData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center text-muted-foreground gap-2">
                  <Euro className="w-8 h-8 opacity-30" />
                  <p className="text-sm text-center">{t('dashboard.noValueData')}</p>
                  <p className="text-xs opacity-70">{t('dashboard.noValueDataHint')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Results */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('dashboard.recentResults')}</CardTitle>
                <CardDescription>
                  {t('dashboard.recentResultsDescription')}
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/results')}>
                {t('dashboard.viewAll')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : recentResults.length > 0 ? (
                <div className="space-y-4">
                  {recentResults.map((result) => (
                    <div
                      key={result.id}
                      className="flex items-start gap-4 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                      onClick={() => navigate('/results')}
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <FileSearch className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground truncate">
                          {result.title}
                        </h4>
                        <p className="text-sm text-muted-foreground truncate">
                          {result.buyer_name} • {result.country}
                        </p>
                        <div className="flex items-center gap-4 mt-1">
                          {result.estimated_value && (
                            <span className="text-sm font-medium text-primary">
                              €{result.estimated_value.toLocaleString()}
                            </span>
                          )}
                          {result.deadline_date && (
                            <span className="text-xs text-muted-foreground">
                              {t('dashboard.expires')}: {formatRelativeTime(result.deadline_date)}
                            </span>
                          )}
                        </div>
                      </div>
                      {!result.is_read && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary text-primary-foreground">
                          {t('dashboard.new')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    {t('dashboard.noResults')}
                  </p>
                  <Button onClick={() => navigate('/monitoring')}>
                    {t('dashboard.configureMonitoring')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Trends Analysis Section */}
        <TrendsAnalysis />
      </div>
    </DashboardLayout>
  );
}
